<template>
  <div
    v-if="isAvailableToVisible"
    :id="id"
    class="page-content"
  >
    <div
      class="card"
      :class="{ collapsed: collapsed }"
    >
      <div
        v-if="!noTitle"
        class="card-header"
      >
        <h3 class="card-title">
          <span><slot name="title"> Component Title </slot></span>
          <div
            v-if="isOperatorDashboard"
            class="favorite-icon"
            :class="isFavoriteForm ? 'active' : ''"
            @click="onUpdateFavoriteForm"
          >
            <i class="fa fa-heart" />
          </div>
        </h3>
        <div class="additional-actions">
          <div class="additional-action-wrapper">
            <slot name="additional-actions" />
          </div>
          <div
            v-if="showCollapse"
            class="action-icon collapse-icon"
            :class="{ rotate: collapsed }"
            @click="collapseContent"
          >
            <i class="fa fa-angle-up" />
          </div>
          <div
            v-if="showClose"
            class="action-icon"
            @click="close"
          >
            <i class="fa fa-close" />
          </div>
        </div>
      </div>

      <hr
        v-if="!noTitle"
        class="card-title-line"
      >

      <div
        class="card-content"
        :class="{ collapsed: collapsed }"
      >
        <customized-form
          v-loading="isLoading"
          element-loading-custom-class="custom-spinner-class"
          :element-loading-text="loadingMessage"
          @submit="$emit('submit')"
        >
          <slot name="card-notes" />

          <slot
            v-if="isTopButtonLayout"
            name="top-form"
          />

          <slot name="top-result-message" />

          <slot name="content" />

          <slot />

          <div
            v-if="!noAction && isBottomButtonLayout"
            class="form-submit-button-group"
          >
            <hr class="mb-10">

            <p-button
              v-if="!hideSubmit"
              type="purple"
              round
              native-type="submit"
              :disabled="disabled"
            >
              <slot name="submit-button">
                Submit
              </slot>
            </p-button>

            <slot name="additional-buttons" />

            <p-button
              v-if="!hideReset"
              type="yellow"
              round
              @click="resetForm"
            >
              <slot name="reset-button">
                Reset
              </slot>
            </p-button>
          </div>

          <slot name="footer" />
        </customized-form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, toRefs } from 'vue';
import { useRoute } from 'vue-router';

import util from '@/Utils/util';
import { catchErrorMessageBox } from '@/Utils/catchErrorMessageBox';
import { useProfile } from '@/use/profile';
import { useDashboard } from '@/use/app';
import { formList } from '@/Utils/formList';
import { addFavoriteForm } from '@/services/userFavoriteForm/addUserFavoriteForm';
import { removeFavoriteForm } from '@/services/userFavoriteForm/removeUserFavoriteForm';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  noAction: {
    type: Boolean,
    default: false,
  },
  hideSubmit: {
    type: Boolean,
    default: false,
  },
  hideReset: {
    type: Boolean,
    default: false,
  },
  showClose: {
    type: Boolean,
    default: false,
  },
  showCollapse: {
    type: Boolean,
    default: false,
  },
  loadingMessage: {
    type: String,
    default: 'Submitting form...',
  },
  buttonLocation: {
    type: String,
    validator: (prop: string) => prop === 'top' || prop === 'bottom',
    default: 'bottom',
  },
  noTitle: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'reset', 'close']);

const { isFamilyDashboard, isOperatorDashboard } = useDashboard();
const { userRoles, favoriteForms, setFavoriteForms } = useProfile();
const { roles } = toRefs(userRoles);

const collapsed = ref(false);

const isTopButtonLayout = computed(() => props.buttonLocation === 'top');

const isBottomButtonLayout = computed(() => !isTopButtonLayout.value);

const isAvailableToVisible = computed(() => {
  if (isFamilyDashboard.value) {
    return true;
  }

  if (!props.id) {
    return true;
  }

  const form = formList.find((item) => item.id === props.id);

  if (!form || !form.roles || form.roles.length === 0) {
    return true;
  }

  return roles.value.some((userRole: string) => form.roles?.includes(userRole));
});

const isFavoriteForm = computed(() => favoriteForms.value.includes(props.id));

const resetForm = () => {
  emit('reset');
};

const close = () => {
  emit('close');
};

const collapseContent = () => {
  collapsed.value = !collapsed.value;
};

const onUpdateFavoriteForm = () => {
  if (isFavoriteForm.value) {
    onRemoveUserFavoriteForm();
  } else {
    onAddUserFavoriteForm();
  }
};

const onAddUserFavoriteForm = async () => {
  catchErrorMessageBox(async () => {
    const result = await addFavoriteForm(props.id);

    if (!result) {
      return;
    }

    setFavoriteForms([...favoriteForms.value, props.id]);
  }).catch(() => null);
};

const onRemoveUserFavoriteForm = async () => {
  catchErrorMessageBox(async () => {
    const result = await removeFavoriteForm(props.id);

    if (!result) {
      return;
    }

    setFavoriteForms(favoriteForms.value.filter((formId: string) => formId !== props.id));
  }).catch(() => null);
};

onMounted(async () => {
  const route = useRoute();
  const { hash } = route;

  if (!hash || hash !== `#${props.id}`) {
    return;
  }

  await nextTick();

  util.moveScrollComponent(hash);
});
</script>

<style lang="scss">
.page-content {
  .card {
    &.collapsed {
      padding-bottom: 10px;
    }
    .card-title-line {
      margin-top: 5px;
      margin-bottom: 0;
      @media (max-width: 576px) {
        margin-top: 0;
        margin-bottom: 10px;
      }
    }
    .card-header {
      display: flex;
      justify-content: space-between;
      .card-title {
        margin: 0;
        color: #252422;
        font-weight: 300;
        font-size: 24px;
        display: flex;
        align-items: center;
        @media (max-width: 576px) {
          text-align: center;
        }

        .favorite-icon {
          font-size: 18px;
          color: #4957a6;
          margin-left: 5px;
          cursor: pointer;

          &.active {
            color: #f00;
          }
        }
      }
      .additional-action {
        display: flex;
        align-items: center;
      }
      .action-icon {
        color: #000;
        font-size: 16px;
        opacity: 0.5;
        cursor: pointer;
        margin-left: 10px;
        &:hover {
          color: #c1c1c1;
        }

        &.collapse-icon {
          width: 30px;
          height: 30px;
          border: 1px solid #000;
          text-align: center;
          border-radius: 50%;
          line-height: 25px;
          font-size: 25px;

          &.rotate {
            transform: rotate(180deg);
          }
        }
      }
    }
    .card-content {
      padding: 15px 15px 10px;
      &.collapsed {
        display: none;
      }
      @media (max-width: 576px) {
        padding: 0 10px 10px;
      }
    }
    .card-notes {
      padding-bottom: 10px;
      line-height: 20px;
      font-weight: 600;
      margin-bottom: 20px;
      border-bottom: 1px solid #e8e8e8;
      text-align: left;
      @media (max-width: 576px) {
        margin-bottom: 10px;
      }
    }
  }
}
</style>
